import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Alert,
  Autocomplete,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTitle, ListLayoutPanel } from '../../../../components/common';
import { VoucherService } from '../services/voucherService';
import { PlanCuentaService } from '../../plan-cuentas/services/planCuentaService';
import type { Voucher, VoucherDetalle } from '../types/voucher';
import { FUENTES_CONTABLES, MONEDAS, initialVoucher, initialVoucherDetalle } from '../types/voucher';
import type { PlanCuenta } from '../../plan-cuentas/types/planCuenta';

const VoucherFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'nuevo';

  const [voucher, setVoucher] = useState<Partial<Voucher>>({ ...initialVoucher, detalles: [] });
  const [cuentasMovimiento, setCuentasMovimiento] = useState<PlanCuenta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCuentas();
    if (!isNew && id) {
      loadVoucher(parseInt(id));
    }
  }, [id]);

  const loadCuentas = async () => {
    try {
      const data = await PlanCuentaService.getCuentasMovimiento();
      setCuentasMovimiento(data);
    } catch (err) {
      // handled
    }
  };

  const loadVoucher = async (voucherId: number) => {
    setLoading(true);
    try {
      const data = await VoucherService.getById(voucherId);
      setVoucher(data);
    } catch (err) {
      setError('Error al cargar el voucher');
    } finally {
      setLoading(false);
    }
  };

  const handleHeaderChange = (field: string, value: any) => {
    setVoucher(prev => ({ ...prev, [field]: value }));
  };

  const handleAddDetalle = () => {
    const detalles = [...(voucher.detalles || [])];
    detalles.push({
      ...initialVoucherDetalle,
      idVoucherDetalle: 0,
      idVoucher: voucher.idVoucher || 0,
      numeroLinea: detalles.length + 1,
      codigoCuenta: '',
      debeMN: 0,
      haberMN: 0,
      debeME: 0,
      haberME: 0,
    } as VoucherDetalle);
    setVoucher(prev => ({ ...prev, detalles }));
  };

  const handleDetalleChange = (index: number, field: string, value: any) => {
    const detalles = [...(voucher.detalles || [])];
    detalles[index] = { ...detalles[index], [field]: value };
    setVoucher(prev => ({ ...prev, detalles }));
  };

  const handleRemoveDetalle = (index: number) => {
    const detalles = (voucher.detalles || []).filter((_, i) => i !== index);
    // Renumber lines
    detalles.forEach((d, i) => { d.numeroLinea = i + 1; });
    setVoucher(prev => ({ ...prev, detalles }));
  };

  const handleCuentaChange = (index: number, cuenta: PlanCuenta | null) => {
    if (cuenta) {
      handleDetalleChange(index, 'codigoCuenta', cuenta.codigoCuenta);
      handleDetalleChange(index, 'idPlanCuenta', cuenta.idPlanCuenta);
    } else {
      handleDetalleChange(index, 'codigoCuenta', '');
      handleDetalleChange(index, 'idPlanCuenta', null);
    }
  };

  // Calculate totals
  const totals = (voucher.detalles || []).reduce(
    (acc, d) => ({
      debeMN: acc.debeMN + (d.debeMN || 0),
      haberMN: acc.haberMN + (d.haberMN || 0),
      debeME: acc.debeME + (d.debeME || 0),
      haberME: acc.haberME + (d.haberME || 0),
    }),
    { debeMN: 0, haberMN: 0, debeME: 0, haberME: 0 }
  );

  const diferenciaMN = Math.abs(totals.debeMN - totals.haberMN);
  const diferenciaME = Math.abs(totals.debeME - totals.haberME);
  const isCuadrado = diferenciaMN < 0.01 && diferenciaME < 0.01;

  const handleSave = async () => {
    if (!isCuadrado) {
      setError('El asiento debe estar cuadrado (Debe = Haber)');
      return;
    }
    if (!voucher.detalles || voucher.detalles.length === 0) {
      setError('Debe agregar al menos un detalle');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const dataToSave = {
        ...voucher,
        totalDebeMN: totals.debeMN,
        totalHaberMN: totals.haberMN,
        totalDebeME: totals.debeME,
        totalHaberME: totals.haberME,
      };
      if (isNew) {
        await VoucherService.create(dataToSave);
      } else {
        await VoucherService.update(parseInt(id!), dataToSave);
      }
      navigate('/contabilidad/vouchers');
    } catch (err) {
      setError('Error al guardar el voucher');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ListLayoutPanel>
      <PageTitle
        title={isNew ? 'Nuevo Voucher' : `Voucher ${voucher.numeroRegistro || ''}`}
        subtitle="Asiento contable"
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate('/contabilidad/vouchers')}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading || !isCuadrado}
            >
              Guardar
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Fuente</InputLabel>
              <Select
                value={voucher.codigoFuente || 'LD'}
                label="Fuente"
                onChange={(e) => handleHeaderChange('codigoFuente', e.target.value)}
              >
                {FUENTES_CONTABLES.map(f => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Fecha Movimiento"
              type="date"
              value={voucher.fechaMovimiento ? voucher.fechaMovimiento.substring(0, 10) : ''}
              onChange={(e) => handleHeaderChange('fechaMovimiento', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Moneda</InputLabel>
              <Select
                value={voucher.monedaOrigen || 'MN'}
                label="Moneda"
                onChange={(e) => handleHeaderChange('monedaOrigen', e.target.value)}
              >
                {MONEDAS.map(m => (
                  <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Tipo de Cambio"
              type="number"
              value={voucher.tipoCambio || 1}
              onChange={(e) => handleHeaderChange('tipoCambio', parseFloat(e.target.value))}
              inputProps={{ step: '0.0001', min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Glosa"
              value={voucher.glosa || ''}
              onChange={(e) => handleHeaderChange('glosa', e.target.value)}
              required
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Tipo Documento"
              value={voucher.tipoDocumento || ''}
              onChange={(e) => handleHeaderChange('tipoDocumento', e.target.value)}
              inputProps={{ maxLength: 5 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Serie"
              value={voucher.serieDocumento || ''}
              onChange={(e) => handleHeaderChange('serieDocumento', e.target.value)}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Número Documento"
              value={voucher.numeroDocumento || ''}
              onChange={(e) => handleHeaderChange('numeroDocumento', e.target.value)}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Detail Lines */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Detalle del Asiento
          </Typography>
          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={handleAddDetalle}
            variant="outlined"
          >
            Agregar Línea
          </Button>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell width={300}>Cuenta</TableCell>
                <TableCell width={130} align="right">Debe MN</TableCell>
                <TableCell width={130} align="right">Haber MN</TableCell>
                <TableCell width={130} align="right">Debe ME</TableCell>
                <TableCell width={130} align="right">Haber ME</TableCell>
                <TableCell>Glosa Detalle</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(voucher.detalles || []).map((detalle, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={cuentasMovimiento}
                      getOptionLabel={(opt) => `${opt.codigoCuenta} - ${opt.descripcion}`}
                      value={cuentasMovimiento.find(c => c.codigoCuenta === detalle.codigoCuenta) || null}
                      onChange={(_e, newVal) => handleCuentaChange(index, newVal)}
                      renderInput={(params) => <TextField {...params} variant="standard" placeholder="Cuenta" />}
                      disableClearable={false}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      value={detalle.debeMN || ''}
                      onChange={(e) => handleDetalleChange(index, 'debeMN', parseFloat(e.target.value) || 0)}
                      inputProps={{ style: { textAlign: 'right' }, step: '0.01' }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      value={detalle.haberMN || ''}
                      onChange={(e) => handleDetalleChange(index, 'haberMN', parseFloat(e.target.value) || 0)}
                      inputProps={{ style: { textAlign: 'right' }, step: '0.01' }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      value={detalle.debeME || ''}
                      onChange={(e) => handleDetalleChange(index, 'debeME', parseFloat(e.target.value) || 0)}
                      inputProps={{ style: { textAlign: 'right' }, step: '0.01' }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      type="number"
                      value={detalle.haberME || ''}
                      onChange={(e) => handleDetalleChange(index, 'haberME', parseFloat(e.target.value) || 0)}
                      inputProps={{ style: { textAlign: 'right' }, step: '0.01' }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      variant="standard"
                      value={detalle.glosaDetalle || ''}
                      onChange={(e) => handleDetalleChange(index, 'glosaDetalle', e.target.value)}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveDetalle(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2} align="right">
                  <Typography fontWeight="bold">Totales:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{totals.debeMN.toFixed(2)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{totals.haberMN.toFixed(2)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{totals.debeME.toFixed(2)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold">{totals.haberME.toFixed(2)}</Typography>
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={2} align="right">
                  <Typography fontWeight="bold" color={isCuadrado ? 'success.main' : 'error.main'}>
                    Diferencia:
                  </Typography>
                </TableCell>
                <TableCell colSpan={2} align="center">
                  <Typography fontWeight="bold" color={diferenciaMN < 0.01 ? 'success.main' : 'error.main'}>
                    MN: {diferenciaMN.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell colSpan={2} align="center">
                  <Typography fontWeight="bold" color={diferenciaME < 0.01 ? 'success.main' : 'error.main'}>
                    ME: {diferenciaME.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell colSpan={2}></TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </ListLayoutPanel>
  );
};

export default VoucherFormPage;
